import React, { useMemo } from "react";
import { Typography } from "antd";
import { CheckCircleFilled } from "@ant-design/icons";
import { SbTag } from "uiCore";
import { useMatchThresholds } from "Store/useTalentMobilitySettingsStore";
import {
  buildPersonColumns,
  buildScoreColumn,
  buildShortlistReasonColumn,
} from "../RedeploymentCenterV2/RedeploymentCenterV2.utils";
import ShortlistAvailabilityButton from "./components/ShortlistAvailabilityButton";

const { Text } = Typography;

// Reduction table column builder — the page owns state (cutoff, this
// reduction's availability set), this hook owns presentation. Rank and Status
// come from each person's position in the full occupants ranking (not the
// rendered row index) so both stay correct when column filters hide rows;
// "Available" is answered by the role's redeployment plan, not the
// person-level flag. "Reason" reads the plan entry's shortlist_reason (see
// useShortlistReasonsForRole); onEditReason(record) opens the page's edit
// dialog from the reason popover.
export const useReductionDetailColumns = ({
  cutoff,
  availableEmployeeIds,
  shortlistReasons,
  onEditReason,
  occupants,
  currentRoleJdId,
}) => {
  const thresholds = useMatchThresholds();

  return useMemo(() => {
    const rankByPersonId = new Map(occupants.map((occupant, i) => [occupant.person?.id, i]));
    const statusOf = (record) =>
      rankByPersonId.get(record.person?.id) >= cutoff ? "At Risk" : "Retained";
    const isRetained = (record) => statusOf(record) === "Retained";

    return [
      {
        title: "#",
        key: "rank",
        width: 50,
        render: (_, record) => rankByPersonId.get(record.person?.id) + 1,
      },
      ...buildPersonColumns(occupants),
      buildScoreColumn("SRI (this role)", "matchPct", thresholds, {
        index: "SRI",
        // align: "center" (buildScoreColumn's default) only centers the body
        // cells — the sortable header's title+icon group needs this class
        // too (see RedeploymentReductionDetailV2.styles.js).
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
      }),
      {
        title: "Status",
        key: "status",
        align: "center",
        filters: [
          { text: "Retained", value: "Retained" },
          { text: "At Risk", value: "At Risk" },
        ],
        filterSearch: true,
        onFilter: (value, record) => statusOf(record) === value,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (_, record) =>
          statusOf(record) === "At Risk" ? (
            <SbTag color="error">At Risk</SbTag>
          ) : (
            <SbTag color="success">Retained</SbTag>
          ),
      },
      {
        title: "Available",
        key: "available",
        align: "center",
        filters: [
          { text: "Yes", value: true },
          { text: "No", value: false },
        ],
        filterSearch: true,
        onFilter: (value, record) => availableEmployeeIds.has(record.person.id) === value,
        onHeaderCell: () => ({ className: "tm-col-header-center" }),
        render: (_, record) =>
          availableEmployeeIds.has(record.person.id) ? (
            <SbTag color="success" icon={<CheckCircleFilled />}>Yes</SbTag>
          ) : (
            <Text type="secondary">No</Text>
          ),
      },
      buildShortlistReasonColumn(shortlistReasons, isRetained, onEditReason),
      {
        title: "",
        key: "shortlistAction",
        align: "center",
        // Row-level shortlist toggle for every occupant, Retained or At Risk
        // — the dialog requires a Reason for Shortlisting only for a
        // Retained one (isRetained, also saved as is_retained). The row
        // keeps its rank and status either way (see orderReductionOccupants)
        // — only Available and Reason change.
        render: (_, record) => (
          <ShortlistAvailabilityButton
            person={record.person}
            currentRoleJdId={currentRoleJdId}
            isRetained={isRetained(record)}
          />
        ),
      },
    ];
  }, [thresholds, cutoff, availableEmployeeIds, shortlistReasons, onEditReason, occupants, currentRoleJdId]);
};
